import React, { useEffect, useState } from "react";
import { db, auth } from "../firebase";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { format } from "date-fns";

const DailyClaimPage = () => {
  const [activePackage, setActivePackage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claimedDays, setClaimedDays] = useState({});

  // Fetch active package for current user
  useEffect(() => {
    
    const fetchActivePackage = async () => {
      const user = auth.currentUser;
      
       if (!user) {
        window.location.href = "/signin";
        return;
      }

      try {
        const activePackageRef = collection(
          db,
          "users",
          user.uid,
          "activePackage"
        );
        const snapshot = await getDocs(activePackageRef);

        if (!snapshot.empty) {
          const docSnap = snapshot.docs[0];
          const pkg = { id: docSnap.id, ...docSnap.data() };
          setActivePackage(pkg);
          setClaimedDays(pkg.claimedDays || {});
        } else {
          setActivePackage(null);
        }
      } catch (err) {
        console.error("Error fetching active package:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchActivePackage();
  }, []);

  // Calculate which day is claimable today
  const getTodayClaimableDay = () => {
    if (!activePackage?.purchasedAt?.seconds) return 0;

    const purchasedAt = new Date(activePackage.purchasedAt.seconds * 1000);
    const now = new Date();

    const diffTime = now - purchasedAt;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    return diffDays + 1; // Day count starts from 1
  };

  // Handle claim click
  const handleClaim = async (day) => {
    const user = auth.currentUser;
    if (!user || !activePackage) return;

    const todayDay = getTodayClaimableDay();

    if (day !== todayDay) {
      return alert("You can only claim today's reward.");
    }

    if (claimedDays[day]) return alert("Already claimed!");

    try {
      const pkgRef = doc(db, "users", user.uid, "activePackage", activePackage.id);

      const newClaimedDays = { ...claimedDays, [day]: true };
      await updateDoc(pkgRef, { claimedDays: newClaimedDays });

      setClaimedDays(newClaimedDays);

      if (day === activePackage.durationDays) {
        const earningPerDay = (activePackage.price * activePackage.incomePercent) / 100;
        const totalEarning = earningPerDay * activePackage.durationDays;

        const userRef = doc(db, "users", user.uid);
        await updateDoc(userRef, {
          totalEarnings: (activePackage.totalEarnings || 0) + totalEarning,
          asset: (activePackage.asset || 0) + totalEarning,
        });

        await deleteDoc(pkgRef);
        setActivePackage(null);
        alert("Package completed. Earnings credited!");
      } else {
        alert(`Day ${day} claimed successfully!`);
      }
    } catch (err) {
      console.error("Error claiming day:", err);
    }
  };

  if (loading) return <p className="text-center mt-10">Loading...</p>;
  if (!activePackage)
    return <p className="text-center mt-10">No active package found.</p>;

  const startDate = activePackage.purchasedAt?.seconds
    ? new Date(activePackage.purchasedAt.seconds * 1000)
    : null;

  const earningPerDay =
    (activePackage.price * activePackage.incomePercent) / 100;

  const todayDay = getTodayClaimableDay();

  return (
    <div className="flex flex-col items-center min-h-screen bg-gray-100 p-6">
      <div className="bg-white p-6 rounded-2xl shadow-lg w-full max-w-2xl">
        <h1 className="text-2xl font-bold text-center mb-4">
          {activePackage.title}
        </h1>
        <p className="text-center text-gray-600 mb-2">
          Purchased At: {startDate ? format(startDate, "dd MMM yyyy") : "N/A"}
        </p>
        <p className="text-center text-gray-600 mb-2">
          Duration: {activePackage.durationDays} days
        </p>
        <p className="text-center text-gray-600 mb-4">
          Daily Earning: <b>{earningPerDay} USDT</b>
        </p>

        <div className="grid grid-cols-2 gap-3">
          {Array.from({ length: activePackage.durationDays }, (_, i) => i + 1).map((day) => {
            const isClaimed = claimedDays[day];
            const isClaimable = day === todayDay && !isClaimed;
            const isLocked = !isClaimable && !isClaimed;

            return (
              <div
                key={day}
                className="flex justify-between items-center bg-gray-50 p-3 rounded-lg shadow-sm"
              >
                <span>Day {day}</span>
                <button
                  className={`px-3 py-1 rounded-lg text-sm ${
                    isClaimed
                      ? "bg-green-500 text-white cursor-not-allowed"
                      : isClaimable
                      ? "bg-blue-500 text-white hover:bg-blue-600"
                      : "bg-gray-400 text-white cursor-not-allowed"
                  }`}
                  onClick={() => handleClaim(day)}
                  disabled={isLocked || isClaimed}
                >
                  {isClaimed ? "Claimed" : isClaimable ? "Claim" : "Locked"}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DailyClaimPage;
